import '@kitware/vtk.js/Rendering/Profiles/Volume.js';
import { Events, ViewportType } from '../../../enums/index.js';
import eventTarget from '../../../eventTarget.js';
import createLinearRGBTransferFunction from '../../../utilities/createLinearRGBTransferFunction.js';
import invertRgbTransferFunction from '../../../utilities/invertRgbTransferFunction.js';
import { updateOpacity as updateVolumeOpacity } from '../../../utilities/colormap.js';
import uuidv4 from '../../../utilities/uuidv4.js';
import createVolumeActor from '../../helpers/createVolumeActor.js';
import { idleSampleDistanceMultiplierOf, sampleDistanceOf, sampleDistanceRatiosOf, setIdleSampleDistanceMultiplier, } from '../../helpers/createVolumeMapper.js';
import { defaultVolumeStrategyProvider, selectFirstReadyStrategy, } from '../../helpers/volumeRenderStrategy.js';
import { gpuSettingsForRender } from '../../../utilities/gpuSettingsPresets.js';
import { canvasToWorldContextPool, worldToCanvasContextPool, } from '../../helpers/vtkCanvasCoordinateTransforms.js';
import applyVolume3DCamera from './applyVolume3DCamera.js';
import { getInitialVolume3DCamera } from './vtkVolume3DInitialCamera.js';
import { isSegmentationOverlayData, isVolumeLoading, readBoundQuality, } from '../renderingFidelity.js';
import setVtkCameraClippingRange from '../setVtkCameraClippingRange.js';
export class VtkVolume3DRenderPath {
    constructor(provideStrategies = defaultVolumeStrategyProvider, selectStrategy = selectFirstReadyStrategy) {
        this.provideStrategies = provideStrategies;
        this.selectStrategy = selectStrategy;
    }
    async addData(ctx, data, options) {
        const payload = data;
        const hadVolume = ctx.vtk.renderer.getVolumes().length > 0;
        const actor = await createVolumeActor({
            volumeId: payload.volumeId,
            provideStrategies: this.provideStrategies,
            selectStrategy: this.selectStrategy,
        }, ctx.viewport.element, ctx.viewportId, true);
        const mapper = actor.getMapper();
        ctx.vtk.renderer.addVolume(actor);
        if (!hadVolume) {
            const initialCamera = getInitialVolume3DCamera(ctx, payload.imageVolume);
            if (initialCamera) {
                applyCamera(ctx, initialCamera);
            }
        }
        setCameraClippingRange(ctx);
        const defaultRange = actor
            .getProperty()
            .getRGBTransferFunction(0)
            .getRange();
        const rendering = {
            renderMode: 'vtkVolume3d',
            actorEntryUID: uuidv4(),
            actor,
            defaultVOIRange: defaultRange
                ? { lower: defaultRange[0], upper: defaultRange[1] }
                : undefined,
            imageVolume: payload.imageVolume,
            mapper,
            viewportId: ctx.viewportId,
            excludeFromFidelity: isSegmentationOverlayData(payload, options),
        };
        rendering.refreshFidelity = () => this.refreshFidelity(rendering);
        this.provisionStrategies(rendering, 'initial');
        this.applyStrategy(rendering);
        rendering.removeStreamingSubscriptions = subscribeToVolumeEvents(payload.volumeId, (eventType) => {
            this.onVolumeData(rendering, eventType);
            ctx.display.requestRender();
        });
        return {
            rendering,
            updateDataPresentation: (props) => {
                this.updateDataPresentation(rendering, props);
            },
            applyViewState: (camera) => {
                this.applyViewState(ctx, camera);
            },
            getFrameOfReferenceUID: () => {
                return this.getFrameOfReferenceUID(rendering);
            },
            getImageData: () => {
                return this.getImageData(rendering);
            },
            render: () => {
                this.render(ctx, rendering);
            },
            resize: () => {
                this.resize(ctx);
            },
            removeData: () => {
                this.removeData(ctx, rendering);
            },
        };
    }
    updateDataPresentation(rendering, props) {
        applyDataPresentation(rendering, props);
    }
    applyViewState(ctx, camera) {
        applyCamera(ctx, camera);
    }
    canvasToWorld(ctx, canvasPos) {
        return canvasToWorldContextPool({
            canvas: ctx.vtk.canvas,
            renderer: ctx.vtk.renderer,
            canvasPos,
        });
    }
    worldToCanvas(ctx, worldPos) {
        return worldToCanvasContextPool({
            canvas: ctx.vtk.canvas,
            renderer: ctx.vtk.renderer,
            worldPos,
        });
    }
    getFrameOfReferenceUID(rendering) {
        return rendering.imageVolume.metadata?.FrameOfReferenceUID;
    }
    getImageData(rendering) {
        return buildVolumeImageData(rendering.imageVolume);
    }
    onVolumeData(rendering, eventType) {
        if (eventType === Events.IMAGE_VOLUME_LOADING_COMPLETED) {
            this.provisionStrategies(rendering, 'loaded');
            this.applyStrategy(rendering);
            return;
        }
        if (!rendering.excludeFromFidelity) {
            rendering.fidelityStale = true;
        }
    }
    refreshFidelity(rendering) {
        if (!rendering.fidelityStale) {
            return;
        }
        rendering.fidelityStale = false;
        rendering.voxelQuality = this.readQuality(rendering);
        rendering.qualityPending = isVolumeLoading(rendering.imageVolume);
    }
    render(ctx, rendering) {
        if (rendering) {
            this.applyStrategy(rendering);
        }
        ctx.display.requestRender();
    }
    provisionStrategies(rendering, reason) {
        const held = rendering.strategies ?? [];
        const built = this.provideStrategies({
            volume: rendering.imageVolume,
            gpuSettings: gpuSettingsForRender(),
            viewportId: rendering.viewportId,
            reason,
        });
        const strategies = built.map((fresh) => held.find((existing) => existing.name === fresh.name) ?? fresh);
        for (const existing of held) {
            if (!strategies.includes(existing)) {
                existing.deactivate();
            }
        }
        rendering.strategies = strategies;
        if (rendering.strategy && !strategies.includes(rendering.strategy)) {
            rendering.strategy = undefined;
            rendering.boundTexture = undefined;
        }
    }
    applyStrategy(rendering) {
        const volume = rendering.imageVolume;
        if (!volume?.voxelGrid) {
            return;
        }
        const selected = this.selectStrategy({
            strategies: rendering.strategies ?? [],
            previous: rendering.strategy,
            volume,
            viewportId: rendering.viewportId,
        });
        if (selected !== rendering.strategy) {
            rendering.strategy?.deactivate();
            selected?.activate();
            rendering.strategy = selected;
        }
        selected?.update();
        this.bindStrategy(rendering, selected);
    }
    bindStrategy(rendering, strategy) {
        const bindings = strategy?.bindings() ?? [];
        const base = bindings.find((binding) => binding.role === 'base');
        if (base && base.texture !== rendering.boundTexture) {
            rendering.mapper.setScalarTexture?.(base.texture);
            const imageData = rendering.mapper.getInputData?.();
            if (imageData) {
                rendering.mapper.setSampleDistance(sampleDistanceOf(imageData, base.texture, idleSampleDistanceMultiplierOf(rendering.mapper)));
            }
            rendering.mapper.modified();
            rendering.boundTexture = base.texture;
        }
        if (base) {
            rendering.boundGrid = { grid: base.grid, statistic: base.statistic };
            rendering.reductionCause = strategy.reductionCause;
        }
        rendering.voxelQuality = this.readQuality(rendering);
        rendering.qualityPending = isVolumeLoading(rendering.imageVolume);
        rendering.fidelityStale = false;
        rendering.fullGrid = rendering.imageVolume?.voxelGrid;
        const ratios = sampleDistanceRatiosOf(rendering.mapper, rendering.boundTexture);
        rendering.sampleDistanceLod = ratios.current;
        rendering.sampleDistanceIdle = ratios.idle;
    }
    readQuality(rendering) {
        return readBoundQuality(rendering.imageVolume, rendering.boundGrid);
    }
    resize(ctx) {
        ctx.display.requestRender();
    }
    removeData(ctx, rendering) {
        const { actor, removeStreamingSubscriptions } = rendering;
        rendering.strategy?.deactivate();
        rendering.strategy = undefined;
        rendering.strategies = undefined;
        removeStreamingSubscriptions?.();
        ctx.vtk.renderer.removeVolume(actor);
    }
}
export class VtkVolume3DPath {
    constructor() {
        this.id = 'volume3d:vtk-volume';
        this.type = ViewportType.VOLUME_3D_NEXT;
    }
    matches(data, options) {
        return data.type === 'image' && options.renderMode === 'vtkVolume3d';
    }
    createRenderPath() {
        return new VtkVolume3DRenderPath();
    }
    selectContext(rootContext) {
        return rootContext;
    }
}
function subscribeToVolumeEvents(volumeId, onProgress) {
    const handleProgress = (evt) => {
        const detail = evt.detail;
        if (detail?.volumeId !== volumeId) {
            return;
        }
        onProgress(evt.type);
    };
    eventTarget.addEventListener(Events.IMAGE_VOLUME_MODIFIED, handleProgress);
    eventTarget.addEventListener(Events.IMAGE_VOLUME_LOADING_COMPLETED, handleProgress);
    return () => {
        eventTarget.removeEventListener(Events.IMAGE_VOLUME_MODIFIED, handleProgress);
        eventTarget.removeEventListener(Events.IMAGE_VOLUME_LOADING_COMPLETED, handleProgress);
    };
}
function applyDataPresentation(rendering, props) {
    const { actor, defaultVOIRange } = rendering;
    const property = actor.getProperty();
    const voiRange = props?.voiRange ?? defaultVOIRange;
    actor.setVisibility(props?.visible === false ? false : true);
    if (props?.opacity !== undefined) {
        updateVolumeOpacity(actor, props.opacity);
    }
    if (!voiRange) {
        return;
    }
    const transferFunction = createLinearRGBTransferFunction(voiRange);
    if (props?.invert) {
        invertRgbTransferFunction(transferFunction);
    }
    property.setRGBTransferFunction(0, transferFunction);
    if (props?.interpolationType !== undefined) {
        property.setInterpolationType(props.interpolationType);
    }
    if (props?.sampleDistanceMultiplier !== undefined) {
        applySampleDistanceMultiplier(rendering.mapper, props.sampleDistanceMultiplier);
    }
}
function applyCamera(ctx, camera) {
    applyVolume3DCamera(ctx, camera);
    if (camera && camera.clippingRange === undefined) {
        setCameraClippingRange(ctx);
    }
}
function applySampleDistanceMultiplier(mapper, multiplier) {
    const imageData = mapper.getInputData?.();
    if (!imageData) {
        return;
    }
    setIdleSampleDistanceMultiplier(mapper, multiplier);
    const safeMultiplier = Number.isFinite(multiplier)
        ? Math.max(multiplier, 0.001)
        : 1;
    const texture = mapper.getScalarTexture?.();
    mapper.setSampleDistance(sampleDistanceOf(imageData, texture, safeMultiplier));
}
function setCameraClippingRange(ctx) {
    setVtkCameraClippingRange(ctx.vtk.renderer.getActiveCamera());
}
function buildVolumeImageData(imageVolume) {
    const vtkImageData = imageVolume.imageData;
    if (!vtkImageData) {
        return;
    }
    return {
        dimensions: vtkImageData.getDimensions(),
        spacing: vtkImageData.getSpacing(),
        origin: vtkImageData.getOrigin(),
        direction: vtkImageData.getDirection(),
        imageData: vtkImageData,
        metadata: {
            Modality: imageVolume.metadata?.Modality,
            FrameOfReferenceUID: imageVolume.metadata?.FrameOfReferenceUID,
        },
        get scalarData() {
            return imageVolume.voxelManager?.getScalarData();
        },
        scaling: imageVolume.scaling,
        hasPixelSpacing: imageVolume.hasPixelSpacing,
        voxelManager: imageVolume.voxelManager,
    };
}
